import { SkeletonLoader, useToastContext } from '@payaca/ambrosia';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { getJobsByDealId } from '@/utils/stateAccessors';
import { ProposalsPermissions } from '@payaca/permissions/proposals/proposals.permissions';

import { isInvoice } from '@payaca/helpers/jobStatusHelper';

import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';
import * as changeProposalActions from '@payaca/store/proposals/proposalsActions';

import {
  canAddChangeProposalToDeal,
  canAddProposalToDeal,
} from '@payaca/helpers/dealHelper';

import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';
import './DealProposals.sass';

import { useSelector } from '@/api/state';

import SectionHeader from '@payaca/components/sectionHeader/SectionHeader';
import {
  useChangeProposalsForDeal,
  useDeal,
} from '@payaca/store/hooks/appState';
import { requestGetChangeProposalsForDeal } from '@payaca/store/proposals/proposalsActions';
import { ChangeProposal } from '@payaca/types/changeProposalTypes';
import { Link } from 'react-router-dom';
import useGetProjectByIdOverview from '../../../api/queries/project/useGetProjectByIdOverview';
import ChangeProposalCard from '../changeProposalCard/ChangeProposalCard';
import DealProposalHistoryTable from '../dealProposalHistoryTable/DealProposalHistoryTable';
import EditJobModal from '../editJobModal/EditJobModal';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import ProposalCard from '../proposalCard/ProposalCard';

interface Props {
  dealId: number;
  onDealUpdateSuccess: (updatedJobIds?: number[]) => void;
}

export const DealProposals: FC<Props> = ({
  dealId,
  onDealUpdateSuccess,
}: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [showEditWarningModal, setShowEditWarningModal] = useState(false);
  const [isCreatingChangeProposal, setIsCreatingChangeProposal] =
    useState(false);
  const { project } = useGetProjectByIdOverview(dealId.toString());
  const jobs = useSelector((state) => {
    return getJobsByDealId(state, dealId);
  });

  const deal = useDeal(dealId);
  const isFetchingDeal = useSelector(
    (state) => state.deals?.deals && state.deals.deals[dealId]?.isFetching
  );
  const proposals = useMemo(() => {
    return jobs?.filter((x) => !isInvoice(x.status) && !x.isProtoInvoice);
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: jobs.length
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: jobs.length
  }, [jobs, jobs?.length]);

  const activeProposal = useMemo(() => {
    return proposals.find((x) => !x.archivedAt && !x.inactivatedAt);
  }, [proposals]);

  const changeProposals = useChangeProposalsForDeal(dealId);

  useEffect(() => {
    dispatch(requestGetChangeProposalsForDeal(dealId));
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [dealId]);

  useEffect(() => {
    if (activeProposal?.jobContentId) {
      dispatch(
        jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
          activeProposal.jobContentId
        )
      );
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [activeProposal?.jobContentId]);

  const navigateToProposal = (proposalId: number) => {
    history.push(`/quotes/${proposalId}`);
  };

  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React hook useCallback depends on `navigateToChangeProposal`, which changes every render
  const navigateToChangeProposal = (
    changeProposalId: number,
    isEdit?: boolean
  ) => {
    history.push(
      `/projects/${project?.publicId}/changeproposals/${changeProposalId}${
        isEdit ? '/edit' : ''
      }`
    );
  };

  const acceptedProposalElement = useMemo(() => {
    const acceptedChangeProposal = changeProposals
      .sort(
        (a: ChangeProposal, b: ChangeProposal) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      .find((cp: ChangeProposal) => {
        return !cp.voidedAt && !cp.declinedAt && cp.acceptedAt;
      });

    if (acceptedChangeProposal)
      return (
        <ChangeProposalCard
          changeProposalId={acceptedChangeProposal.id}
          onClick={() => navigateToChangeProposal(acceptedChangeProposal.id)}
        />
      );
    const acceptedProposal = proposals.find(
      (x) => !x.archivedAt && !x.inactivatedAt && x.acceptedAt
    );

    return acceptedProposal ? (
      <ProposalCard
        proposalId={acceptedProposal.id}
        onClick={() => navigateToProposal(acceptedProposal.id)}
        onDealUpdateSuccess={onDealUpdateSuccess}
      />
    ) : undefined;
  }, [
    proposals,
    changeProposals,
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React hook useMemo depends on `navigateToProposal`, which changes every render
    navigateToProposal,
    onDealUpdateSuccess,
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React hook useMemo depends on `navigateToChangeProposal`, which changes every render
    navigateToChangeProposal,
  ]);

  const pendingProposalElement = useMemo(() => {
    const pendingChangeProposal = changeProposals
      .sort(
        (a: ChangeProposal, b: ChangeProposal) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
      .find((cp: ChangeProposal) => {
        return !cp.voidedAt && !cp.declinedAt && !cp.acceptedAt;
      });

    if (pendingChangeProposal)
      return (
        <ChangeProposalCard
          changeProposalId={pendingChangeProposal.id}
          onClick={() => navigateToChangeProposal(pendingChangeProposal.id)}
        />
      );
    const pendingProposal = proposals.find(
      (x) => !x.archivedAt && !x.inactivatedAt && !x.acceptedAt
    );

    return pendingProposal ? (
      <ProposalCard
        proposalId={pendingProposal.id}
        onClick={() => navigateToProposal(pendingProposal.id)}
        onDealUpdateSuccess={onDealUpdateSuccess}
      />
    ) : undefined;
  }, [
    proposals,
    changeProposals,
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React hook useMemo depends on `navigateToProposal`, which changes every render
    navigateToProposal,
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React hook useMemo depends on `navigateToChangeProposal`, which changes every render
    navigateToChangeProposal,
    onDealUpdateSuccess,
  ]);

  const { pushError } = useToastContext();

  const handleProposeChange = useCallback(() => {
    if (!deal) {
      return;
    }
    if (canAddChangeProposalToDeal(deal, proposals, changeProposals)) {
      setIsCreatingChangeProposal(true);
      dispatch(
        changeProposalActions.requestPersistChangeProposal(
          { dealId: deal.id },
          (changeProposalId: number) => {
            setIsCreatingChangeProposal(false);
            navigateToChangeProposal(changeProposalId, true);
          },
          () => {
            setIsCreatingChangeProposal(false);
          }
        )
      );
    } else {
      pushError(
        <>
          <strong>Unable to propose a change</strong> You cannot propose a
          change whilst there are outstanding pending changes
        </>
      );
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'pushError', and 'dispatch'
  }, [deal, proposals, changeProposals, navigateToChangeProposal]);

  const actionsComponent = useMemo(() => {
    if (isFetchingDeal) {
      return <SkeletonLoader.Title className="w-20" />;
    }
    if (canAddProposalToDeal(deal) || !proposals?.length) {
      return (
        <Link to={`/quotes/new?dealId=${dealId}`}>
          <Button styleVariant={ButtonStyleVariant.ANCHOR}>
            Create Proposal
          </Button>
        </Link>
      );
    }

    if (activeProposal && !deal?.changeProposalIds.length) {
      if (!activeProposal.sentAt) {
        return (
          <Link to={`/quotes/${activeProposal.id}`}>
            <Button styleVariant={ButtonStyleVariant.ANCHOR}>
              Edit Proposal
            </Button>
          </Link>
        );
      } else if (!activeProposal.acceptedAt) {
        return (
          <Button
            styleVariant={ButtonStyleVariant.ANCHOR}
            onClick={() => setShowEditWarningModal(true)}
          >
            Edit Proposal
          </Button>
        );
      }
    }

    return (
      <PermissionGuard
        renderIfHasPermissions={[ProposalsPermissions.PERSIST_CHANGE_PROPOSAL]}
      >
        <Button
          styleVariant={ButtonStyleVariant.ANCHOR}
          onClick={isCreatingChangeProposal ? undefined : handleProposeChange}
          isProcessing={isCreatingChangeProposal}
        >
          Propose change
        </Button>
      </PermissionGuard>
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'activeProposal', 'dealId', 'proposals.length', 'activeProposal.id', 'activeProposal.sentAt', and 'activeProposal.acceptedAt'
  }, [handleProposeChange, deal, isCreatingChangeProposal, isFetchingDeal]);

  return (
    <div className="deal-proposals">
      <>
        <section className="proposals">
          <SectionHeader
            headerComponent={<h3>Proposals</h3>}
            actionsComponent={actionsComponent}
          />
          <div className="proposals-cards-wrapper">
            {acceptedProposalElement}
            {pendingProposalElement}
          </div>
        </section>
        {!!proposals?.length && (
          <section className="history">
            <SectionHeader headerComponent={<h3>History</h3>} />
            <DealProposalHistoryTable
              dealId={dealId}
              navigateToProposal={navigateToProposal}
              navigateToChangeProposal={navigateToChangeProposal}
            />
          </section>
        )}
        {activeProposal && (
          <EditJobModal
            isOpen={showEditWarningModal}
            onClose={() => setShowEditWarningModal(false)}
            jobId={activeProposal.id}
          />
        )}
      </>
    </div>
  );
};
